import type {PriceTypeV1} from '@smart/price-preferences-provider';
import {TextButton} from '@smart/web-components';
import * as React from 'react';
import type {CashBuyContent} from '../../bff/app-model/fetch-app-model';
import type {PriceDetailType} from '../../bff/price-details';
import type {DelayedStateResult} from '../hooks/use-delayed-state';
import type {MaybePriceDetails} from '../hooks/use-price-details';
import {usePurchaseItemContext} from '../hooks/use-purchase-item-context';
import styles from './cash-buy.module.scss';
import {LegalDisclaimers} from './legal-disclaimers';
import {PaymentDetails} from './payment-details';
import {PaymentSummary} from './payment-summary';

export interface CashBuyProps {
  readonly content: CashBuyContent;
  readonly priceDetailsResult: DelayedStateResult<MaybePriceDetails>;
  readonly priceType: PriceTypeV1;
  readonly onApply: () => void;
  readonly onErrorRetry: () => void;
}

export function CashBuy({
  priceDetailsResult,
  priceType,
  onApply,
  onErrorRetry,
  content,
}: CashBuyProps): JSX.Element {
  const purchaseItem = usePurchaseItemContext();

  const {
    paymentDetailsText,
    paymentDetailsCartText = paymentDetailsText,
    totalPriceLabel,
    totalPriceCartLabel = totalPriceLabel,
    totalPriceTemplate,
    totalPriceFetchError,
    applyButton,
    legalDisclaimer,
  } = content;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onApply();
  };

  const getPrice = React.useCallback(
    (priceDetails: PriceDetailType) => {
      return totalPriceTemplate.replace(
        `{{totalPrice}}`,
        priceDetails?.price?.formattedValue ?? ``,
      );
    },
    [totalPriceTemplate],
  );

  const paymentDetails =
    purchaseItem.type === `cart-item`
      ? paymentDetailsCartText
      : paymentDetailsText;

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {paymentDetails && <PaymentDetails text={paymentDetails} />}
      <PaymentSummary
        label={
          purchaseItem.type === `cart-item`
            ? totalPriceCartLabel
            : totalPriceLabel
        }
        errorMessage={totalPriceFetchError}
        priceDetailsResult={priceDetailsResult}
        priceType={priceType}
        getPrice={getPrice}
        onErrorRetry={onErrorRetry}
      />

      <TextButton
        className={styles.applyButton}
        type="submit"
        variant="primary"
        size="300"
      >
        {applyButton.children}
      </TextButton>

      {legalDisclaimer && legalDisclaimer.length > 0 && (
        <LegalDisclaimers legalDisclaimers={legalDisclaimer} />
      )}
    </form>
  );
}
